import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useQuery } from '@apollo/client';
import PubSub from 'pubsub-js';
import hive from '@bombas/hive';
import LoginForm from '../LoginForm/LoginForm';
import SignUpForm from '../SignUpForm/SignUpForm';
import ForgotPasswordForm from '../ForgotPasswordForm/ForgotPasswordForm';
import UpdatePasswordForm from '../UpdatePasswordForm/UpdatePasswordForm';
import FormSubmissionSuccess from '../../../FormSubmissionSuccess/FormSubmissionSuccess';
import ConfirmationCodeForm from '../ConfirmationCodeForm/ConfirmationCodeForm';
import SignUpSuccess from '../SignUpSuccess/SignUpSuccess';

import { STEPS, useAuthState } from '../../Authentication.context';
import { SITE_CONFIG } from '../../../App.gql';
import { isPreview } from '../../../../graphql/previews';

import * as Styled from './AuthenticationBody.styled';
import ActivateForm from '../ActivateForm/ActivateForm';
import { vercel_base_url } from '../../../../util/config';
import { getCartCheckoutUrlKey } from '../../Authentication.utils';

const {
  ACTIVATE,
  ACTIVATE_CONFIRM,
  ACTIVATE_SUCCESS,
  SIGNUP,
  LOGIN,
  LOGIN_SUCCESS,
  CONFIRM,
  FORGOT,
  UPDATE,
  UPDATE_SUCCESS,
  WELCOME
} = STEPS;

/**
 * Standalone component to allow users to complete an authentication flow
 */
const AuthenticationBody = ({
  onRequestClose,
  onSuccess,
  showPageTitle,
  ...props
}) => {
  const { currentStep, setCurrentStep } = useAuthState();
  const [email, setEmail] = useState();
  const [authAttemptType, setAuthAttemptType] = useState();
  const apolloClient = useApolloClient();
  const { data } = useQuery(SITE_CONFIG, {
    variables: { key: 'auth', preview: isPreview('settings') }
  });
  const config = data?.settings[0]?.values;
  const loyaltyCoupon = config?.loyalty_coupon?.value;
  const isLoyaltyCouponEnabled = config?.is_loyalty_coupon_enabled?.value;

  const handleClose = () => {
    if (onRequestClose) {
      onRequestClose();
    } else {
      window.location.replace(`${vercel_base_url}/account`);
    }
  };

  const isValidUrl = (url) => {
    if (/^https:\/\/(.*\.)?bombas\.(com|fyi)\//.test(url)) {
      return true;
    }
    if (/^\/[^/\\]/.test(url)) {
      return true;
    }
    return false;
  };

  const redirectOrCallback = async (client, cb) => {
    const params = new URLSearchParams(window.location.search);
    const existingCheckoutUrl = params.get('checkout_url');
    let redirectUrl = existingCheckoutUrl || params.get('return_url');
    if (existingCheckoutUrl && client) {
      const authenticatedUrlString = await getCartCheckoutUrlKey(client);
      if (authenticatedUrlString) {
        const authenticatedUrl = new URL(authenticatedUrlString);
        const checkoutUrl = new URL(existingCheckoutUrl, 'https://placeholder.com');
        // ensure our new url passes through all existing params
        checkoutUrl?.searchParams?.forEach((value, key) => {
          // shopify uses 'key' to redirect to an authed checkout experience
          if (key !== 'key') authenticatedUrl.searchParams.set(key, value);
        });
        redirectUrl = authenticatedUrl.toString();
      }
    }
    if (redirectUrl && isValidUrl(redirectUrl)) {
      window.location.replace(redirectUrl);
    } else {
      cb();
    }
  };

  const renderStep = () => {
    let Component;

    switch (currentStep.id) {
      case ACTIVATE.id:
        Component = (
          <ActivateForm
            onComplete={({ id, method }) => {
              if (isLoyaltyCouponEnabled) {
                PubSub.publish('cart.discountApply', loyaltyCoupon);
              }

              hive.track('CUSTOMER_ACTIVATE', { customer: { id, method } });
              setCurrentStep(ACTIVATE_SUCCESS);
            }}
            onPasswordResetRequired={(userEmail) => {
              setAuthAttemptType(ACTIVATE.id);
              setEmail(userEmail);
              setCurrentStep(ACTIVATE_CONFIRM);
            }}
          />
        );
        break;

      case LOGIN.id:
        Component = (
          <LoginForm
            onComplete={async (account) => {
              hive.track('CUSTOMER_LOGIN', { customer: { id: account?.email, method: 'bombas', bombas_id: account?.bombas_id } });

              await redirectOrCallback(apolloClient, () => {
                if (window.location.pathname.indexOf('/account') >= 0)
                  setCurrentStep(LOGIN_SUCCESS);
                else if (
                  window.location.pathname.indexOf('/pages/buy-again') >= 0
                )
                  handleClose();
                else {
                  handleClose();
                  if (onSuccess) onSuccess();
                  if (!currentStep.hideAccountNav)
                    PubSub.publish('account_nav.open');
                }
              });
            }}
            onForgotPasswordClick={() => {
              setAuthAttemptType(FORGOT.id);
              setCurrentStep(FORGOT);
            }}
            onSignUpClick={() => {
              setCurrentStep(SIGNUP);
            }}
            onPasswordResetRequired={(userEmail) => {
              setAuthAttemptType(LOGIN.id);
              setEmail(userEmail);
              setCurrentStep(CONFIRM);
            }}
          />
        );
        break;

      case LOGIN_SUCCESS.id:
        Component = (
          <FormSubmissionSuccess
            message={currentStep.body}
            buttonText={currentStep.cta}
            onComplete={handleClose}
          />
        );
        break;

      case ACTIVATE_SUCCESS.id:
        Component = (
          <FormSubmissionSuccess
            message={currentStep.body}
            useShopButtons
            onComplete={handleClose}
          />
        );
        break;

      case SIGNUP.id:
        Component = (
          <SignUpForm
            onComplete={async ({ id, method }) => {
              hive.track('CUSTOMER_SIGNUP', { customer: { id, method } });

              await redirectOrCallback(apolloClient, () => {
                if (onSuccess) {
                  handleClose();
                  onSuccess();
                } else {
                  setCurrentStep(WELCOME);
                }
              });
            }}
            onLoginClick={() => {
              setCurrentStep(LOGIN);
            }}
            onPasswordResetRequired={(userEmail) => {
              setAuthAttemptType(SIGNUP.id);
              setEmail(userEmail);
              setCurrentStep(CONFIRM);
            }}
          />
        );
        break;

      case CONFIRM.id:
        Component = (
          <ConfirmationCodeForm
            email={email}
            onComplete={() => {
              setCurrentStep(UPDATE);
            }}
            onReject={() => setCurrentStep(LOGIN)}
          />
        );
        break;
      case ACTIVATE_CONFIRM.id:
        Component = (
          <ConfirmationCodeForm
            email={email}
            onComplete={() => {
              setCurrentStep(UPDATE);
            }}
            onReject={() => setCurrentStep(FORGOT)}
          />
        );
        break;

      case FORGOT.id:
        Component = (
          <ForgotPasswordForm
            onComplete={(userEmail) => {
              setEmail(userEmail);
              setCurrentStep(CONFIRM);
            }}
            onReject={() => setCurrentStep(LOGIN)}
          />
        );
        break;

      case UPDATE.id:
        Component = (
          <UpdatePasswordForm
            email={email}
            onComplete={() => {
              if (authAttemptType === ACTIVATE.id && isLoyaltyCouponEnabled) {
                PubSub.publish('cart.discountApply', loyaltyCoupon);
              }

              hive.track('CUSTOMER_PASSWORD_UPDATE', { customer: { authAttempt: authAttemptType } });

              if (onSuccess) {
                onSuccess();
                handleClose();
              } else {
                setCurrentStep(
                  authAttemptType === ACTIVATE.id
                    ? ACTIVATE_SUCCESS
                    : UPDATE_SUCCESS
                );
              }
            }}
          />
        );
        break;

      case UPDATE_SUCCESS.id:
        Component = (
          <FormSubmissionSuccess
            message={currentStep.body}
            buttonText={currentStep.cta}
            onComplete={async () => {
              await redirectOrCallback(apolloClient, () => {
                handleClose();

                if (
                  window.location.pathname.indexOf('/pages/buy-again') < 0 &&
                  window.location.pathname.indexOf('/pages/forgot-password') <
                    0 &&
                  window.location.pathname.indexOf('/account') < 0
                ) {
                  PubSub.publish('account_nav.open');
                }
              });
            }}
          />
        );
        break;

      case WELCOME.id:
        Component = <SignUpSuccess onContinueShopping={handleClose} />;
        break;

      default:
        Component = null;
        break;
    }

    return Component;
  };

  return (
    <Styled.AuthenticationBody {...props}>
      {showPageTitle && <Styled.Title>{currentStep.title}</Styled.Title>}
      {renderStep()}
    </Styled.AuthenticationBody>
  );
};

AuthenticationBody.propTypes = {
  /**
   * function to run when the modal is closed
   */
  onRequestClose: PropTypes.func,
  /**
   * optional call back to run on successful authentication
   * used to refresh checkout page
   */
  onSuccess: PropTypes.func,
  /**
   * `true` to display the page title on a full page experience
   */
  showPageTitle: PropTypes.bool
};

AuthenticationBody.defaultProps = {
  onRequestClose: undefined,
  showPageTitle: false,
  onSuccess: undefined
};

export default AuthenticationBody;
